import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, Boxes, Receipt, ArrowUpRight } from "lucide-react";
import SectionTitle from "@/components/SectionTitle";

const FEATURES = [
  {
    icon: ClipboardList,
    title: "Digital Rx",
    desc: "A structured Rx with tooth chart, materials, shade, and notes. No more vague case notes.",
  },
  {
    icon: Boxes,
    title: "Case Tracking",
    desc: "See every case at every stage, in one live view. No more calling the lab to ask.",
  },
  {
    icon: Receipt,
    title: "Billing",
    desc: "Create an invoice straight from the Rx. No double entry, no reconciling later.",
  },
];

export default function FeatureGrid() {
  return (
    <section data-testid="feature-grid" className="relative py-16 sm:py-20 bg-secondary/30 dark:bg-secondary/20 border-y hairline">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTitle
          title="Core Features"
          description="Tools that work together as one product."
        />

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-3xl hairline glass p-6 lg:p-7"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute -inset-8 bg-brand-radial blur-2xl opacity-60" />
                </div>

                <div className="relative flex flex-col h-full">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl hairline glass inline-flex items-center justify-center">
                      <Icon className="h-4.5 w-4.5 text-brand-plum" />
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                      {i + 1}
                    </div>
                  </div>

                  <h3 className="mt-6 font-heading text-xl lg:text-2xl font-semibold tracking-[-0.01em]">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center sm:justify-start">
          <Link
            to="/features"
            data-testid="feature-grid-see-all"
            className="group inline-flex items-center gap-1.5 text-sm font-medium hairline rounded-full px-4 py-2 hover:bg-secondary transition-colors"
          >
            See all 13 features
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
